/**
 * lp-015, integrity family. Mechanical defects, found mechanically.
 *
 * No model. Every check here is a parsing problem over a document we already
 * hold as structured sections, so a regex finds it faster, cheaper,
 * deterministically, and with nothing to hallucinate. That is also what makes
 * these the checks that can run continuously: the reader is a deliberate act
 * you wait for, and these cost nothing.
 *
 * The catalogue this implements says of the numerals check, "do this in code
 * rather than by reading — this check is high-volume, purely mechanical, and
 * easy to miss by eye, which is exactly why it is worth running." That applies
 * to the whole family.
 *
 * Ordered by trust rather than by value. One bad finding costs a reader more
 * than five good ones earn, so the families that ship first are the ones whose
 * false-positive rate is near zero: a `[TBD]` in a lease is a defect under
 * every reading, and "Ten Thousand Dollars ($16,411.25)" disagrees with itself
 * whatever the provision is about.
 */
import { findingId } from './document_checks';

export const INTEGRITY_FAMILY = 'integrity';

// ── E. Drafting artifacts ────────────────────────────────────────────────────

/**
 * An instruction to the person drafting, left in the document.
 *
 * `[USE IF COMMENCEMENT DATE IS TIED TO PERMITS]` and `[... - INCLUDE AN
 * OUTSIDE DATE IF THERE ARE ADDITIONAL CONDITIONS]` are the form telling its
 * author what to do. They match none of the patterns below — `note_to_draft`
 * wants the letters NTD, `placeholder_token` wants TBD or INSERT at the start —
 * so the most consequential artifact in a template-built lease went unreported.
 *
 * Anchored on an ALL-CAPS IMPERATIVE rather than on capitals alone. A bracket
 * full of capitals is as likely to be `[INTENTIONALLY OMITTED]` or `[SIGNATURE
 * PAGE FOLLOWS]`, which are boilerplate and correct; a bracket containing USE,
 * INCLUDE or DELETE is somebody being told to do something.
 */
const DRAFTER_INSTRUCTION_VERBS =
  'USE|INSERT|INCLUDE|ADD|DELETE|REMOVE|CHOOSE|SELECT|CONFIRM|VERIFY|REVIEW|' +
  'CHECK|UPDATE|REVISE|SPECIFY|DESCRIBE|OMIT|RETAIN|KEEP|DRAFT|COMPLETE';

// BOUNDED ON BOTH SIDES, and that is not tidiness. `[^\]]*` … `[^\]]*` around
// an alternation, with no closing bracket to stop on, backtracks catastrophically.
// Measured on `'[USE '.repeat(n)`, unbounded:
//
//     5KB    2.4s
//     12.5KB  37.6s
//     25KB   307s      — five minutes, on one paragraph
//
// This runs on the browser's main thread over document text, so that is the
// drafter's tab frozen while they type, on text a counterparty could have
// written into a redline.
//
// Bounded, the same 25KB takes 0.25s and 100KB takes 1s — linear, and matching
// everything it matched before. 300 characters each side is far more than any
// real instruction: the one this was built for is 94.
const INSTRUCTION_SPAN = 300;

const DRAFTER_INSTRUCTION = new RegExp(
  `\\[[^\\]]{0,${INSTRUCTION_SPAN}}\\b(?:${DRAFTER_INSTRUCTION_VERBS})\\b[^\\]]{0,${INSTRUCTION_SPAN}}\\]`,
  'g',
);

const ARTIFACT_PATTERNS = [
  {
    id: 'drafter_instruction',
    re: DRAFTER_INSTRUCTION,
    what: 'An instruction to the drafter is still in the text',
  },
  {
    id: 'note_to_draft',
    re: /\[\s*(?:NTD|Note to Draft|Note to Form|Drafting Note|Comment)\b[^\]]*\]/gi,
    what: 'A note to the drafter is still in the text',
  },
  {
    id: 'placeholder_token',
    // `INSERT[^\]]*` is bounded for the same reason as the instruction pattern
    // above: an unbounded run inside an alternation is what makes these
    // quadratic on text with many unclosed brackets.
    re: /\[\s*(?:TBD|TBA|INSERT[^\]]{0,200}|DATE|NAME|XX+|#)\s*\]/gi,
    what: 'An unfilled placeholder',
  },
  { id: 'bare_tbd', re: /\b(?:TBD|TBA)\b/g, what: 'An unfilled placeholder' },
  {
    id: 'blank_money',
    re: /\$\s*_{2,}/g,
    what: 'A blank where a figure belongs',
  },
  {
    id: 'blank_percent',
    re: /_{2,}\s*%/g,
    what: 'A blank where a figure belongs',
  },
  {
    id: 'blank_run',
    // The trailing parenthetical is the same number written twice — "_____
    // (___)" is one thing a drafter fills, not two, and counting it as two is
    // how a document with ten blanks reports fifty-three.
    re: /_{4,}(?:\s*\(\s*_{2,}\s*\))?/g,
    what: 'A blank that was never filled in',
  },
  {
    id: 'template_delimiter',
    re: /(?:<<|\{\{|«)[^\n]{0,60}?(?:>>|\}\}|»)/g,
    what: 'A template marker survived assembly',
  },
];

/**
 * Signature lines are blanks on purpose.
 *
 * A signature block, a notary block and an estoppel form are MEANT to arrive
 * with rules on them for somebody to write on. Reporting those turns the
 * highest-volume check in the family into its noisiest, and a drafter who is
 * told eleven times that a signature line is empty stops reading the panel.
 */
// A signature block, not a sentence that mentions signing.
//
// `acknowledg` was in this list and it is the single commonest verb in a lease:
// "Tenant acknowledges that the security deposit is $______" lost BOTH of its
// blanks, and estoppel certificates are built out of that sentence. `attest`
// and `witness` are the same kind of word. What is left is the furniture a
// signature block is actually made of, which ordinary prose does not contain.
const SIGNATURE_CONTEXT =
  /\b(signature|print name|notary public|in witness whereof|by:|its:|title:)/i;

/**
 * Text the author has not chosen yet, and everything that follows from it.
 *
 * A lease built from a form carries alternatives: `[USE IF COMMENCEMENT DATE IS
 * TIED TO PERMITS]: The earlier of (i) ...` is one of two clauses, and the
 * author is meant to keep one and delete the other. Until they do, the document
 * genuinely contains two definitions of "Delivery Date" — and reporting that as
 * a duplicate definition is reporting a SYMPTOM. The defect is the instruction
 * still sitting there; resolve it and the duplicate goes with it.
 *
 * Two shapes, and the first is the one that matters and the one a naive reading
 * misses. The bracket in the example closes BEFORE the clause it introduces, so
 * "ignore what is inside brackets" would not have covered the second definition
 * at all:
 *
 *   - a block that OPENS with an instruction is provisional in its entirety
 *   - an instruction bracketed mid-sentence makes that span provisional
 *
 * Findings inside a provisional range are suppressed — except the instruction
 * itself, which is the one thing worth saying about the whole region.
 */
function provisionalRanges(text, notes) {
  const ranges = [];

  // The author's own mark, and the stronger of the two signals. A highlight in
  // a lease built from a form IS a drafter's note — firms use it for exactly
  // this, to mark what still has to be resolved — and unlike any wording
  // heuristic it does not depend on guessing which capitalised phrases are
  // instructions. `form_outline` carries the highlighted runs as text; they are
  // found again here by content.
  (notes || []).forEach(note => {
    const at = text.indexOf(note);
    if (at !== -1) ranges.push([at, at + note.length]);
  });

  const re = new RegExp(DRAFTER_INSTRUCTION.source, DRAFTER_INSTRUCTION.flags);
  let match;

  while ((match = re.exec(text)) !== null) {
    const from = match.index;
    const to = from + match[0].length;

    // Leading whitespace only before it: the instruction introduces the block
    // rather than qualifying a phrase inside it, so the block is an alternative
    // and everything in it is provisional.
    if (!text.slice(0, from).trim()) ranges.push([from, text.length]);
    else ranges.push([from, to]);
  }

  return ranges;
}

function inProvisional(ranges, from, to) {
  return ranges.some(range => from < range[1] && (to ?? from + 1) > range[0]);
}

function isSignatureLine(text) {
  return SIGNATURE_CONTEXT.test(text);
}

/**
 * A blank is not a defect one at a time.
 *
 * An unfilled form is made of them — one sentence of a basic-provisions grid
 * carries three — and the first real run put a separate finding on every one,
 * which buries every other family under a list of underscores. Nobody fixes a
 * blank by reading about that blank; they fill the document in. So the count is
 * the finding, and the quote carries the reader to the first of them.
 *
 * A note to the drafter is the opposite: each says something different and has
 * to be read on its own.
 */
const COLLAPSED = new Set(['blank_run', 'blank_money', 'blank_percent']);

function artifactFindings(sections) {
  const findings = [];
  const collapsed = new Map();

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const text = block.text;
      // What some earlier pattern has already claimed. `$______` is a blank
      // where a figure belongs AND a run of underscores; without this it is
      // both, and every money blank is reported twice. The patterns are ordered
      // most specific first, so first claim wins.
      const claimed = [];
      const overlaps = (from, to) =>
        claimed.some(span => from < span[1] && to > span[0]);
      // Text the author has not chosen yet. The blanks and placeholders inside
      // an unresolved alternative are part of the alternative, not defects of
      // their own — a form offering two clauses has blanks in both, and
      // reporting them doubles the count on every template-built lease.
      const provisional = provisionalRanges(text, block.notes);

      ARTIFACT_PATTERNS.forEach(pattern => {
        const re = new RegExp(pattern.re.source, pattern.re.flags);
        let match;

        while ((match = re.exec(text)) !== null) {
          const hit = match[0].trim();
          if (pattern.id.startsWith('blank') && isSignatureLine(text)) continue;
          if (overlaps(match.index, match.index + match[0].length)) continue;
          // The instruction is the one thing worth saying about the region it
          // governs, so it is never suppressed by its own range.
          if (
            pattern.id !== 'drafter_instruction' &&
            inProvisional(provisional, match.index, match.index + match[0].length)
          )
            continue;
          claimed.push([match.index, match.index + match[0].length]);

          const at = {
            pid: block.pid || section.pid || null,
            number: section.number || null,
            order,
          };

          if (COLLAPSED.has(pattern.id)) {
            const group = collapsed.get(pattern.id) || { pattern, hits: [] };
            group.hits.push({
              blockText: text,
              hit,
              index: match.index,
              length: hit.length,
              at,
            });
            collapsed.set(pattern.id, group);
            continue;
          }

          findings.push({
            id: findingId(
              `integrity_${pattern.id}`,
              section.pid || String(order),
              hit,
            ),
            family: INTEGRITY_FAMILY,
            check: pattern.id,
            severity: 'medium',
            summary: `${pattern.what} — ${truncate(hit, 40)}`,
            statement: `${pattern.what}: ${hit}`,
            quote: excerpt(text, match.index, hit.length),
            at,
            // The literal, so the panel can highlight the words themselves
            // rather than the paragraph they happen to sit in.
            hits: [{ pid: at.pid, text: hit }],
            proposal: null,
          });
        }
      });
    });
  });

  collapsed.forEach((group, id) => {
    const first = group.hits[0];
    const count = group.hits.length;
    const where = first.at.number
      ? `section ${first.at.number}`
      : 'the opening pages';

    findings.push({
      id: findingId(`integrity_${id}`, 'document', String(count)),
      family: INTEGRITY_FAMILY,
      check: id,
      severity: 'medium',
      summary:
        count === 1
          ? `${group.pattern.what}, in one place`
          : `${group.pattern.what}, in ${count} places`,
      statement:
        count === 1
          ? `${group.pattern.what}.`
          : `${group.pattern.what}. There are ${count}, the first in ${where}.`,
      quote: excerpt(first.blockText, first.index, first.length),
      at: first.at,
      // Every one of them. Selecting "a blank in 152 places" should show all
      // 152, not send the drafter to the first and leave them hunting.
      hits: group.hits.map(h => ({ pid: h.at.pid, text: h.hit })),
      proposal: null,
    });
  });

  return findings;
}

// ── G. Numerals vs words ─────────────────────────────────────────────────────

const UNITS = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};
const SCALES = { hundred: 100, thousand: 1000, million: 1e6, billion: 1e9 };

/**
 * "Ten Thousand and 00/100" → 10000, or null when the words are not a number.
 *
 * Null is the common answer and has to stay cheap: most parentheticals in a
 * lease follow ordinary prose, and a parser that guessed at those would report
 * a disagreement between a number and a sentence.
 */
export function wordsToNumber(phrase) {
  const words = String(phrase || '')
    .toLowerCase()
    .replace(/\band\s+\d{1,2}\/100\b/g, ' ')
    .split(/[\s-]+/)
    .filter(word => word && word !== 'and');
  if (!words.length) return null;

  let total = 0;
  let current = 0;
  let sawNumber = false;

  for (const word of words) {
    if (UNITS[word] !== undefined) {
      current += UNITS[word];
      sawNumber = true;
    } else if (SCALES[word] !== undefined) {
      if (!sawNumber) return null;
      const scale = SCALES[word];
      if (scale === 100) current *= 100;
      else {
        total += current * scale;
        current = 0;
      }
    } else {
      // Any word that is not part of a number means this was not one.
      return null;
    }
  }

  return sawNumber ? total + current : null;
}

// The parenthetical figure. What precedes it is found by walking backwards,
// not by a lazy quantifier: `<words>\s*\(` matches the SHORTEST run ending at
// the paren, which on "Ten Thousand and 00/100 Dollars ($16,411.25)" captures
// "Dollars" and on "within thirty (60) days" captures "within thirty". The
// number words are not adjacent to the bracket and cannot be assumed to be.
const FIGURE = /\(\s*\$?\s*([\d,]+(?:\.\d+)?)\s*%?\s*\)/g;

// Words that sit between the spelled number and its figure. "Dollars" and
// "percent" are the common ones; a lease also writes "and 00/100" for cents.
const TRAILING_UNITS = new Set([
  'dollars',
  'dollar',
  'percent',
  'days',
  'day',
  'months',
  'month',
  'years',
  'year',
  'times',
  'square',
  'feet',
  'foot',
]);

/**
 * The spelled number immediately before a figure, or null.
 *
 * Walks back over the unit words, then collects consecutive number words and
 * stops at the first word that is neither. Stopping is what keeps "within
 * thirty" from being read as a number phrase beginning with "within".
 */
function isNumberWord(word) {
  return word
    .split('-')
    .filter(Boolean)
    .every(part => UNITS[part] !== undefined || SCALES[part] !== undefined);
}

function spelledBefore(text, index) {
  const words = text
    .slice(0, index)
    .trim()
    .split(/[\s]+/);
  const collected = [];

  for (let i = words.length - 1; i >= 0; i -= 1) {
    const word = words[i].toLowerCase().replace(/[^a-z0-9/-]/g, '');
    if (!word) continue;
    if (
      collected.length === 0 &&
      (TRAILING_UNITS.has(word) ||
        /^\d{1,2}\/100$/.test(word) ||
        word === 'and')
    ) {
      continue;
    }
    // Membership, not parsing. `wordsToNumber('thousand')` is null by design —
    // a bare scale is not a number — so parsing each word in turn stops the
    // walk on the very word that proves it is a number phrase.
    if (!isNumberWord(word)) break;

    collected.unshift(words[i]);
    if (collected.length > 8) break;
  }

  return collected.length ? wordsToNumber(collected.join(' ')) : null;
}
const MONEY_NEAR = /\bdollars?\b|\$/i;
const TIME_NEAR = /\b(days?|months?|years?)\b/i;

function numeralFindings(sections) {
  // Same rule as everywhere else: a figure inside an alternative nobody has
  // chosen is not yet a figure this document states.
  const findings = [];

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const text = block.text;
      const provisional = provisionalRanges(text, block.notes);
      const re = new RegExp(FIGURE.source, FIGURE.flags);
      let match;

      while ((match = re.exec(text)) !== null) {
        if (inProvisional(provisional, match.index, match.index + match[0].length))
          continue;

        const spelled = spelledBefore(text, match.index);
        if (spelled === null) continue;

        const figure = Number(match[1].replace(/,/g, ''));
        if (!Number.isFinite(figure)) continue;
        if (spelled === figure) continue;

        const context = text.slice(
          Math.max(0, match.index - 30),
          match.index + match[0].length + 30,
        );
        // Money and the day counts that govern a cure or a notice period are
        // the ones that change what the document means when they disagree.
        const severity =
          MONEY_NEAR.test(context) || TIME_NEAR.test(context)
            ? 'high'
            : 'medium';

        findings.push({
          id: findingId(
            'integrity_numerals',
            section.pid || String(order),
            match[0].trim(),
          ),
          family: INTEGRITY_FAMILY,
          check: 'numerals_vs_words',
          severity,
          summary: `Written amount and figure disagree — ${truncate(
            match[0].trim(),
            44,
          )}`,
          statement: `The words say ${spelled} and the figure says ${figure}.`,
          quote: excerpt(text, match.index, match[0].length),
          at: {
            pid: block.pid || section.pid,
            number: section.number,
            order,
          },
          hits: [
            { pid: block.pid || section.pid, text: match[0].trim() },
          ],
          proposal: null,
        });
      }
    });
  });

  return findings;
}

// ── ────────────────────────────────────────────────────────────────────────

export function integrityFindings(sections) {
  // A null entry in the outline threw out of `blocksOf` and took the whole
  // panel with it. Filtered once here rather than guarded in nine places.
  const readable = (Array.isArray(sections) ? sections : []).filter(Boolean);
  return distinct([
    ...artifactFindings(readable),
    ...numeralFindings(readable),
    ...referenceFindings(readable),
    ...numberingFindings(readable),
    ...definitionFindings(readable),
    ...entityFindings(readable),
  ]);
}

/**
 * Identity has to be unique, and the first real run proved it was not.
 *
 * The same defect twice in one section produced the same id, and `ng-repeat`
 * refuses duplicate keys outright — `[ngRepeat:dupes]`, and the panel does not
 * render at all. One bad id took down every finding on the screen.
 *
 * A suffix rather than the character offset, which was the other candidate. An
 * offset changes whenever anything earlier in the paragraph is edited, so every
 * decision on that finding would be lost to an unrelated typo. An occurrence
 * number only moves when a matching occurrence is added or removed before it.
 */
function distinct(findings) {
  const seen = new Map();

  return findings.map(finding => {
    const count = (seen.get(finding.id) || 0) + 1;
    seen.set(finding.id, count);
    return count === 1 ? finding : { ...finding, id: `${finding.id}#${count}` };
  });
}

function paragraphsOf(section) {
  return blocksOf(section).map(block => block.text);
}

/**
 * The same blocks, each still carrying the pid it came from.
 *
 * A finding marks the clause it is about, and `paragraphsOf` was throwing the
 * pid away — so every mark landed on the section's heading instead. On a long
 * article that is pages from the thing being reported: open "[Insert Here]" and
 * the highlight appears on "ARTICLE 62 — MISCELLANEOUS".
 */
function blocksOf(section) {
  const blocks = [];
  const keep = text => typeof text === 'string' && text.trim();

  if (keep(section.title))
    blocks.push({ text: section.title, pid: section.pid || null, notes: [] });

  (section.paragraphs || []).forEach(paragraph => {
    if (paragraph && keep(paragraph.text))
      blocks.push({
        text: paragraph.text,
        pid: paragraph.pid || section.pid || null,
        // Highlighted runs, carried from the outline so every family can tell
        // an unresolved alternative from a defect.
        notes: paragraph.notes || [],
      });
  });

  return blocks;
}

function truncate(text, limit) {
  return text.length <= limit ? text : `${text.slice(0, limit - 1)}…`;
}

// Enough either side that the drafter can see what the hit sits in without
// opening the document, and not so much that the list becomes the document.
function excerpt(text, index, length) {
  const start = Math.max(0, index - 60);
  const end = Math.min(text.length, index + length + 60);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${
    end < text.length ? '…' : ''
  }`;
}

// ── B. Cross-references, C. Numbering ────────────────────────────────────────

const ROMAN = { i: 1, v: 5, x: 10, l: 50, c: 100 };

function fromRoman(text) {
  const chars = text.toLowerCase().split('');
  if (!chars.every(c => ROMAN[c] !== undefined)) return null;

  let total = 0;
  chars.forEach((c, i) => {
    const value = ROMAN[c];
    const next = ROMAN[chars[i + 1]];
    total += next && next > value ? -value : value;
  });
  return total || null;
}

/**
 * "ARTICLE VII" and "Article 7" are the same target, so both become "7".
 * "12.3" stays "12.3", and "EXHIBIT B-1" becomes "B-1".
 */
export function normalizeRef(kind, raw) {
  const text = String(raw || '')
    .trim()
    .replace(/[.,;:]+$/, '');
  if (!text) return null;

  if (kind === 'exhibit') return text.toUpperCase();
  if (/^\d/.test(text)) return text.replace(/\.$/, '');

  // Whole first, then without a suffix. The suffix strip exists for "ARTICLE
  // 1A", where a letter was appended rather than the sixty following articles
  // renumbered — but applied blind it eats the last numeral of "IX" and calls
  // Article 9 Article 1.
  const whole = fromRoman(text);
  if (whole) return String(whole);

  const stem = fromRoman(text.replace(/[A-Z]$/i, ''));
  return stem ? String(stem) : text.toUpperCase();
}

// CASE-SENSITIVE LABELS, case-insensitive words. The `i` flag used to cover the
// whole pattern, which also made `[A-Z0-9]` and `[\dIVXL]` match lowercase — so
// "the rider attached" read as Rider "attached", "the addendum described" as
// Addendum "described", and "the paragraph in question" as Paragraph "i". Every
// one of those became a broken-reference finding on ordinary lease prose, and
// three of them together were enough to be reported as a renumbering.
//
// A label in a lease is capitalised — Exhibit A, Article IV, Section 4.2 — so
// requiring that is not a restriction worth the false positives it buys. The
// leading word still matches either case, written out rather than flagged.
const SECTION_WORDS =
  '[Ss]ections?|[Aa]rticles?|[Pp]aragraphs?|[Cc]lauses?';
const ATTACHMENT_WORDS =
  '[Ee]xhibits?|[Ss]chedules?|[Rr]iders?|[Aa]ddend(?:um|a)|[Aa]ppendix|[Aa]ppendices';

// `§` sits outside the word alternation: `\b` before `§` can never match, since
// there is no word boundary between a space and a symbol, so the symbol form was
// undetectable while the spelled form worked.
const SECTION_REF = new RegExp(
  `(?:\\b(?:${SECTION_WORDS})\\s+|§{1,2}\\s?)([\\dIVXL]+[A-Za-z]?(?:\\.\\d+)*)`,
  'g',
);
const ATTACHMENT_REF = new RegExp(
  `\\b(?:${ATTACHMENT_WORDS})\\s+([A-Z0-9][-A-Z0-9.]*)`,
  'g',
);

/**
 * A reference to somebody else's document is not a broken reference to ours.
 *
 * "Section 12.3 of the Master Lease" points outside this document and cannot be
 * resolved against it. Left in, this is the single largest source of false
 * positives in the family, because leases talk about other instruments
 * constantly — the guaranty, the prior lease, the SNDA, the declaration.
 */
const FOREIGN = /^\s*(?:of|under|in|to)\s+(?:the\s+)?(?:[A-Z][A-Za-z]*\s+){0,3}(lease|guaranty|guarantee|agreement|declaration|snda|mortgage|deed|contract|note|estoppel)\b/i;

function isForeign(text, endIndex) {
  return FOREIGN.test(text.slice(endIndex, endIndex + 60));
}

// Numbering the document declares anywhere, not only in the headings the
// outline opened a section for. A lease writes "12.3" at the head of a
// paragraph that is not a section in our terms, and a reference to it is not
// broken merely because our outline did not open a section there.
function declaredNumbers(sections) {
  const numbers = new Set();
  const attachments = new Set();

  sections.forEach(section => {
    const heading = String(section.number || '');
    const asAttachment = /^EXHIBIT\s+(.+)$/i.exec(heading);
    if (asAttachment) attachments.add(normalizeRef('exhibit', asAttachment[1]));
    else if (heading)
      numbers.add(
        normalizeRef('section', heading.replace(/^(ARTICLE|SECTION)\s+/i, '')),
      );

    paragraphsOf(section).forEach(text => {
      const lead = /^\s*(\d+(?:\.\d+)*)[.\s)]/.exec(text);
      if (lead) numbers.add(normalizeRef('section', lead[1]));
    });
  });

  numbers.delete(null);
  attachments.delete(null);
  return { numbers, attachments };
}

/**
 * Can we see this depth at all?
 *
 * Our outline opens a section only at the shallowest heading level, so a
 * document whose subsections we never indexed would report every "Section 12.3"
 * as broken — hundreds of findings, all wrong, on the first lease somebody
 * tried. A reference is only reported when we can PROVE we index that depth,
 * by having seen a sibling under the same prefix.
 */
function indexesThatDepth(numbers, ref) {
  const parts = ref.split('.');
  if (parts.length === 1) return true;

  const prefix = parts.slice(0, -1).join('.');
  for (const known of numbers) {
    if (known === ref) continue;
    const knownParts = known.split('.');
    if (
      knownParts.length === parts.length &&
      knownParts.slice(0, -1).join('.') === prefix
    ) {
      return true;
    }
  }
  return false;
}

/**
 * A section that was retired, and is therefore not a place to send a reader.
 *
 * Leases retire a clause by leaving a marker where it stood, precisely so the
 * numbering does not shift — which `numberingFindings` already understands well
 * enough not to complain about the gap. The other half of understanding it is
 * this: the section still declares its number, so every cross-reference to it
 * still RESOLVES, and a clause that says "as provided in Section 22" points at
 * two words and nothing else. `broken_reference` cannot see it, because by its
 * test the target exists.
 *
 * Two shapes, both real. The heading is replaced — `22. INTENTIONALLY OMITTED`
 * — or the heading is kept and the body gutted: `22. ASSIGNMENT. Intentionally
 * Omitted.` A section is retired when its BODY says nothing else; where there
 * is no body, when the heading is the marker.
 *
 * "Reserved" is included and it is the risky one, so it is matched only as the
 * whole of a block. A section headed RESERVED PARKING has a body and is not
 * retired; one whose entire text is the word "Reserved" is.
 */
const OMITTED_MARKER =
  /^[\s\[\(]*(?:intentionally\s+(?:omitted|deleted|left\s+blank)|omitted\s+intentionally|reserved|not\s+used)[\s.\]\)]*$/i;

function isOmittedMarker(text) {
  return OMITTED_MARKER.test(String(text || '').trim());
}

function retiredNumbers(sections) {
  const retired = new Map();

  sections.forEach(section => {
    const heading = String(section.number || '');
    if (!heading || /^EXHIBIT\s+/i.test(heading)) return;

    const paragraphs = (section.paragraphs || [])
      .map(paragraph => (paragraph && paragraph.text) || '')
      .filter(text => text.trim());

    const retiredHere = paragraphs.length
      ? paragraphs.every(isOmittedMarker)
      : isOmittedMarker(section.title);

    if (!retiredHere) return;

    const ref = normalizeRef(
      'section',
      heading.replace(/^(ARTICLE|SECTION)\s+/i, ''),
    );
    if (ref) retired.set(ref, section);
  });

  return retired;
}

/**
 * lp-016 §3, Family B2. "As defined in Section 5.2", where it is not.
 *
 * The semantic pass already has a `misreference` kind, and for the general case
 * — a reference to a section that is simply ABOUT something else — a model is
 * the only thing that can judge it. This is the slice where a model is the
 * wrong tool: the reference makes a CHECKABLE claim. It names a term, it names
 * a section, and whether that section mentions that term is a fact.
 *
 * So this runs on every keystroke, costs nothing, cannot hallucinate, and has
 * no batching limit — the semantic pass can only see a misreference when the
 * target happens to fall in the same batch of six sections as the reference.
 * What is left for the model is the part that genuinely needs reading.
 *
 * THE BAR IS "THE TARGET DOES NOT MENTION THE TERM AT ALL", not "the target
 * does not define it in a form we recognise". A section can define a term
 * without quotation marks — "Base Rent shall be the amount set forth below" —
 * and `definedTermsIn` would not see it. Silence there is correct; a section
 * that never says the words at all is the confident case.
 */
const DEFINED_IN = new RegExp(
  '([“"][^”"]{2,60}[”"]|[A-Z][a-z][A-Za-z]*(?:\\s+of\\s+[A-Z][a-z][A-Za-z]*|\\s+[A-Z][a-z][A-Za-z]*){0,3})' +
    '\\s*[,(]?\\s*[Aa]s\\s+(?:such\\s+term\\s+is\\s+)?(?:more\\s+particularly\\s+)?' +
    '[Dd]efined\\s+in\\s+(?:[Ss]ection|[Aa]rticle|[Pp]aragraph)\\s+([\\d.]+)',
  'g',
);

function misdefinedReferenceFindings(sections, numbers) {
  const byNumber = new Map();
  sections.forEach(section => {
    const heading = String(section.number || '');
    if (!heading) return;
    const ref = normalizeRef('section', heading.replace(/^(ARTICLE|SECTION)\s+/i, ''));
    if (ref && !byNumber.has(ref)) byNumber.set(ref, section);
  });

  const findings = [];

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const re = new RegExp(DEFINED_IN.source, DEFINED_IN.flags);
      let match;

      while ((match = re.exec(block.text)) !== null) {
        const term = termWithin(
          match[1].replace(/[“”"]/g, '').trim().replace(/\s+/g, ' '),
        );
        if (!term) continue;

        const ref = normalizeRef('section', match[2]);
        if (!ref || !numbers.has(ref)) continue;

        // Not indexed at this depth, so we cannot see the target to judge it.
        const target = byNumber.get(ref);
        if (!target) continue;

        const targetText = blocksOf(target)
          .map(targetBlock => targetBlock.text)
          .join(' ');
        const mentions = new RegExp(
          `(^|[^A-Za-z])${escapeForRegex(term)}([^A-Za-z]|$)`,
          'i',
        );
        if (mentions.test(targetText)) continue;

        const hit = match[0].trim();
        findings.push({
          id: findingId(
            'integrity_misdefined_reference',
            block.pid || section.pid || String(order),
            hit,
          ),
          family: INTEGRITY_FAMILY,
          check: 'misdefined_reference',
          severity: 'medium',
          summary: `“${term}” is not defined where this says it is`,
          statement:
            `This points at section ${match[2]} for the definition of “${term}”, ` +
            'and that section does not mention the term at all.',
          quote: excerpt(block.text, match.index, hit.length),
          at: {
            pid: block.pid || section.pid || null,
            number: section.number || null,
            order,
          },
          // The claim and the section it lands on, so both are visible at once.
          hits: [
            { pid: block.pid || section.pid, text: hit },
            target.pid && target.title
              ? { pid: target.pid, text: String(target.title).trim() }
              : null,
          ].filter(Boolean),
          proposal: null,
        });
      }
    });
  });

  return findings;
}

function referenceFindings(sections) {
  const { numbers, attachments } = declaredNumbers(sections);
  const retired = retiredNumbers(sections);
  const broken = [];
  const dangling = [];

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const text = block.text;
      const provisional = provisionalRanges(text, block.notes);
      const sectionRe = new RegExp(SECTION_REF.source, SECTION_REF.flags);
      let match;

      while ((match = sectionRe.exec(text)) !== null) {
        if (isForeign(text, match.index + match[0].length)) continue;
        if (inProvisional(provisional, match.index, match.index + match[0].length))
          continue;

        const ref = normalizeRef('section', match[1]);
        if (!ref) continue;

        // The target exists but has been emptied. Reported against the clause
        // that still depends on it, not against the retired section — the
        // retired section is correct, and it is the reference that needs a
        // decision. One finding per reference, because each needs its own.
        if (retired.has(ref)) {
          dangling.push({
            ref,
            text,
            index: match.index,
            hit: match[0].trim(),
            pid: block.pid || section.pid || null,
            target: retired.get(ref),
            section,
            order,
          });
          continue;
        }

        if (numbers.has(ref)) continue;
        if (!indexesThatDepth(numbers, ref)) continue;

        broken.push({
          ref,
          text,
          index: match.index,
          hit: match[0].trim(),
          pid: block.pid || section.pid || null,
          section,
          order,
        });
      }

      const attachmentRe = new RegExp(
        ATTACHMENT_REF.source,
        ATTACHMENT_REF.flags,
      );
      while ((match = attachmentRe.exec(text)) !== null) {
        if (isForeign(text, match.index + match[0].length)) continue;
        if (inProvisional(provisional, match.index, match.index + match[0].length))
          continue;

        const ref = normalizeRef('exhibit', match[1]);
        // Only when the document attaches exhibits at all. A lease whose
        // exhibits live in a separate file would otherwise report every one.
        if (!ref || !attachments.size || attachments.has(ref)) continue;

        broken.push({
          ref,
          text,
          index: match.index,
          hit: match[0].trim(),
          pid: block.pid || section.pid || null,
          section,
          order,
          attachment: true,
        });
      }
    });
  });

  return renumberingOrIndividual(broken)
    .concat(danglingFindings(dangling))
    .concat(misdefinedReferenceFindings(sections, numbers));
}

/**
 * Not clustered the way `broken_reference` is.
 *
 * A renumbering cluster is several references all off by the same amount, which
 * is one cause with many symptoms. These have no common cause: each clause
 * independently came to depend on a section somebody later emptied, and each is
 * fixed on its own terms — deleted, repointed, or the clause it needed restored.
 */
function danglingFindings(dangling) {
  return dangling.map(b => ({
    id: findingId(
      'integrity_reference_omitted',
      b.section.pid || String(b.order),
      b.hit,
    ),
    family: INTEGRITY_FAMILY,
    check: 'reference_to_omitted',
    severity: 'medium',
    summary: `${b.hit} points at a section that was intentionally omitted`,
    statement:
      `${b.hit} is referenced here, and that section has been intentionally ` +
      'omitted — the number is still in the document but the clause it named is not.',
    quote: excerpt(b.text, b.index, b.hit.length),
    at: {
      pid: b.pid || b.section.pid,
      number: b.section.number,
      order: b.order,
    },
    // The reference and the empty section it lands on, so selecting the finding
    // shows both halves of it.
    hits: [
      { pid: b.pid || b.section.pid, text: b.hit },
      b.target && b.target.pid && b.target.title
        ? { pid: b.target.pid, text: String(b.target.title).trim() }
        : null,
    ].filter(Boolean),
    proposal: null,
  }));
}

/**
 * Three references sharing an offset are one defect, not three.
 *
 * The catalogue's B4: a cluster all pointing the same distance off means the
 * document was renumbered without its cross-references being updated. Reported
 * separately, a reader gets a list of broken links and has to work out the
 * cause; reported together, they get the cause.
 */
function renumberingOrIndividual(broken) {
  const sectionBreaks = broken.filter(
    b => !b.attachment && /^\d+$/.test(b.ref.split('.')[0]),
  );
  const byOffset = new Map();

  sectionBreaks.forEach(b => {
    const parts = b.ref.split('.');
    const last = Number(parts[parts.length - 1]);
    if (!Number.isFinite(last)) return;
    [-1, 1].forEach(offset => {
      const key = offset;
      byOffset.set(key, (byOffset.get(key) || []).concat(b));
    });
  });

  const cluster = [...byOffset.values()].find(group => group.length >= 3);
  if (cluster) {
    const first = cluster[0];
    return [
      {
        id: findingId(
          'integrity_renumbering',
          'document',
          cluster
            .map(b => b.ref)
            .sort()
            .join(','),
        ),
        family: INTEGRITY_FAMILY,
        check: 'renumbering_damage',
        severity: 'high',
        summary: `${cluster.length} cross-references point at sections that do not exist`,
        statement:
          `${cluster.length} references resolve to nothing (${cluster
            .map(b => b.hit)
            .slice(0, 4)
            .join(', ')}${
            cluster.length > 4 ? ', …' : ''
          }). A cluster this size usually means the ` +
          'document was renumbered without its cross-references being updated.',
        quote: excerpt(first.text, first.index, first.hit.length),
        at: {
          pid: first.pid || first.section.pid,
          number: first.section.number,
          order: first.order,
        },
        // Every reference in the cluster, so selecting the finding lights the
        // whole pattern rather than one example of it.
        hits: cluster.map(b => ({
          pid: b.pid || b.section.pid,
          text: b.hit,
        })),
        proposal: null,
      },
    ];
  }

  return broken.map(b => ({
    id: findingId(
      'integrity_reference',
      b.section.pid || String(b.order),
      b.hit,
    ),
    family: INTEGRITY_FAMILY,
    check: b.attachment ? 'orphan_attachment_reference' : 'broken_reference',
    severity: 'medium',
    summary: `${b.hit} does not exist in this document`,
    statement: `${b.hit} is referenced here and nothing in the document carries that number.`,
    quote: excerpt(b.text, b.index, b.hit.length),
    at: {
      pid: b.pid || b.section.pid,
      number: b.section.number,
      order: b.order,
    },
    hits: [{ pid: b.pid || b.section.pid, text: b.hit }],
    proposal: null,
  }));
}

/**
 * A gap is only a defect when nothing stands in its place.
 *
 * Leases retire a section by leaving "Intentionally Omitted" behind precisely so
 * the numbering does not move, and reporting those would flag the document for
 * doing the correct thing.
 */
function numberingFindings(sections) {
  // Keyed by numbering sequence, not by bare number.
  //
  // A lease runs several sequences at once: the Articles, the numbered rules in
  // the Rules and Regulations exhibit, the signage criteria, the warranties in
  // a lien waiver. Each restarts at 1. Collapsing them into one set makes
  // "ARTICLE 2", "2." in the rules and "2." in the signage criteria look like
  // three sections fighting over the same number, when a reference to rule 2
  // inside the rules is not ambiguous at all — and it makes every gap between
  // one sequence's end and another's start read as a missing section.
  //
  // The outline already tells us which sequence a heading belongs to. Two
  // articles really numbered 53 still collide; an article and a rule do not.
  const sequences = new Map();

  sections.forEach((section, order) => {
    const heading = String(section.number || '').replace(
      /^(ARTICLE|SECTION)\s+/i,
      '',
    );
    const ref = normalizeRef('section', heading);
    if (!ref || !/^\d+$/.test(ref)) return;

    const key = String(section.list || 'unsequenced');
    if (!sequences.has(key))
      sequences.set(key, { seen: new Map(), omitted: new Set() });
    const sequence = sequences.get(key);

    const title = `${section.title || ''} ${paragraphsOf(section).join(' ')}`;
    if (/intentionally\s+(omitted|deleted|left blank)/i.test(title))
      sequence.omitted.add(ref);

    sequence.seen.set(ref, (sequence.seen.get(ref) || []).concat({ section, order }));
  });

  const findings = [];

  sequences.forEach(({ seen, omitted }, key) => {
    seen.forEach((entries, ref) => {
      if (entries.length < 2) return;
      const first = entries[0];
      findings.push({
        id: findingId('integrity_duplicate_number', key, ref),
        family: INTEGRITY_FAMILY,
        check: 'duplicate_number',
        severity: 'medium',
        summary: `${entries.length} sections are numbered ${ref}`,
        statement: `${entries.length} sections carry the number ${ref}, so every reference to it is ambiguous.`,
        quote: (first.section.title || '').trim() || null,
        at: {
          pid: first.section.pid,
          number: first.section.number,
          order: first.order,
        },
        // Every heading in the collision, so selecting it shows the drafter
        // both sections wearing the same number rather than one of them.
        hits: entries
          .filter(entry => entry.section.pid && entry.section.number)
          .map(entry => ({
            pid: entry.section.pid,
            text: String(entry.section.number),
          })),
        proposal: null,
      });
    });

    const ordered = [...seen.keys()]
      .map(Number)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    for (let i = 1; i < ordered.length; i += 1) {
      // The heading where the numbering jumps. A gap is the one finding with no
      // text of its own — it is about something that is not there — so it is
      // anchored to the place the absence is visible, which is the heading that
      // should have followed and does not. Without this it was the only family
      // that could not be located, quoted or highlighted, and "there is no
      // section 63" with nowhere to look is a claim a drafter has to take on
      // trust.
      const after = (seen.get(String(ordered[i])) || [])[0];

      for (let missing = ordered[i - 1] + 1; missing < ordered[i]; missing += 1) {
        if (omitted.has(String(missing))) continue;
        findings.push({
          id: findingId('integrity_numbering_gap', key, String(missing)),
          family: INTEGRITY_FAMILY,
          check: 'numbering_gap',
          severity: 'low',
          summary: `There is no section ${missing}`,
          statement: `Numbering runs from ${ordered[i - 1]} to ${
            ordered[i]
          } with nothing in between and no "Intentionally Omitted" in its place.`,
          quote: after ? (after.section.title || '').trim() || null : null,
          at: after
            ? {
                pid: after.section.pid,
                number: after.section.number,
                order: after.order,
              }
            : null,
          hits: after && after.section.number
            ? [{ pid: after.section.pid, text: String(after.section.number) }]
            : [],
          proposal: null,
        });
      }
    }
  });

  return findings;
}

// ── A. Defined terms ─────────────────────────────────────────────────────────

/**
 * lp-016 §3, Family A.
 *
 * The catalogue lists four checks here and this builds two of them. That is a
 * decision, not an omission, and the reason is the same one that governs every
 * family: these are the sender's OWN documents, and a false finding sends a
 * lawyer to reconcile something that was already right.
 *
 * BUILT — defined twice. A term introduced formally in two places is a real
 * defect with no innocent reading: whichever definition a reader reaches first
 * wins, and the drafter almost never intended the second.
 *
 * BUILT — defined and then never used. A leftover from a template cut, low
 * severity because it harms nothing, and worth saying because it is usually the
 * visible end of a clause somebody deleted half of.
 *
 * NOT BUILT — used but never defined. Leases capitalise Landlord, Tenant, Rent
 * and Premises everywhere, define them in a grid on the summary page rather
 * than in a sentence, and inherit dozens more from a form. The check that finds
 * a genuinely undefined term also finds forty that are fine.
 *
 * NOT BUILT — case drift. "base Rent" against "Base Rent" is real, and it is
 * also what ordinary prose looks like at the start of a sentence and inside a
 * quotation. The signal does not survive the noise.
 */

// The two forms a lease actually uses to introduce a term. Parenthetical —
// `(the "Premises")`, `(collectively, the "Buildings")` — and declarative:
// `"Base Rent" shall mean`. Both anchor on quotation marks, straight or smart,
// which is what keeps this out of ordinary capitalised prose.
const QUOTE_OPEN = '["“‘]';
const QUOTE_SHUT = '["”’]';

const PARENTHETICAL = new RegExp(
  `\\(\\s*(?:the\\s+|each\\s+(?:a\\s+|an\\s+)?|collectively(?:,)?\\s*(?:the\\s+)?|individually(?:,)?\\s*(?:a\\s+|an\\s+)?)?` +
    `${QUOTE_OPEN}([^"“”‘’]{2,60}?)${QUOTE_SHUT}\\s*\\)`,
  'g',
);

const DECLARATIVE = new RegExp(
  `${QUOTE_OPEN}([^"“”‘’]{2,60}?)${QUOTE_SHUT}\\s*` +
    `(?:shall\\s+mean|shall\\s+have\\s+the\\s+meaning|means\\b|is\\s+defined|has\\s+the\\s+meaning)`,
  'gi',
);

// A defined term is a noun phrase. These are what a stray quotation mark around
// ordinary prose looks like, and they are not terms.
const NOT_A_TERM = /^(?:and|or|the|a|an|as|if|shall|will|may|to|of|in|no|not)\b/i;

function definedTermsIn(text, notes) {
  const found = [];
  // A definition inside an alternative the author has not chosen is not a
  // second definition yet. This is the case that started it: a lease reported
  // "Delivery Date is defined twice" when the second one sat inside a
  // `[USE IF ...]` block nobody had resolved.
  const provisional = provisionalRanges(text, notes);

  [PARENTHETICAL, DECLARATIVE].forEach(source => {
    const re = new RegExp(source.source, source.flags);
    let match;

    while ((match = re.exec(text)) !== null) {
      const term = match[1].trim().replace(/\s+/g, ' ');
      if (term.length < 2 || NOT_A_TERM.test(term)) continue;
      if (inProvisional(provisional, match.index, match.index + match[0].length))
        continue;
      // A defined term carries a capital. Everything else inside quotation
      // marks in a lease is somebody quoting a statute or a notice.
      if (!/[A-Z]/.test(term.charAt(0))) continue;

      found.push({ term, hit: match[0].trim(), index: match.index });
    }
  });

  return found;
}

/**
 * lp-016 §3, Family A, the two checks this file previously declined.
 *
 * The reason for declining them was real and has not gone away: leases
 * capitalise Landlord, Tenant, Rent and Premises on every page without ever
 * defining them in a sentence, and define half their vocabulary in a grid on
 * the summary page rather than in prose. An open reading of "capitalised but
 * never defined" finds forty innocent terms for every real one.
 *
 * So both are built NARROW, and every narrowing below is a filter against a
 * specific way the open version was wrong.
 *
 * Reported as a fact about THIS DOCUMENT, at low severity. The cause usually
 * lives in the template these leases are generated from, and saying so here
 * would be advice about a form nobody opened — the drafter reading this panel
 * is sending a lease, not maintaining a template. It is an inconsistency in
 * front of them; what to do about it upstream is a separate conversation.
 */

// Two to four Title Case words. `[A-Z][a-z]` rather than `[A-Z]` on purpose: it
// excludes ALL CAPS, which in a lease is a heading or emphasis rather than a
// term in use.
//
// `of` is the only connector, and the omissions matter more than the inclusion.
// With `and` in the list, "Tenant shall pay Operating Expenses and Real Estate
// Taxes" is ONE forty-character term rather than two real ones — so neither
// reaches the use threshold and both go unreported. Every conjunction does that.
// "Letter of Credit" is a term; "X and Y" is two terms in a sentence.
const TERM_SHAPED =
  /\b[A-Z][a-z][A-Za-z]*(?:\s+of\s+[A-Z][a-z][A-Za-z]*|\s+[A-Z][a-z][A-Za-z]*){1,3}\b/g;

// A term has to be used more than once before its absence means anything. A
// single Title Case phrase is a proper noun, a building name, or a heading that
// wandered into a sentence far more often than it is a term somebody forgot to
// define.
const USES_BEFORE_REPORTING = 2;

// A determiner that opened the sentence is not part of the term. Without this,
// "The Letter of Credit" and "a Letter of Credit" are two different four-word
// terms, each used once, and the term they both name is never counted at all.
const LEADING_DETERMINER =
  /^(?:The|A|An|This|That|These|Those|Such|Any|Each|All|No|Its|Their|Our)\s+/;

function termWithin(phrase) {
  const stripped = phrase.replace(LEADING_DETERMINER, '').trim();
  // Stripping can leave one word, and a single capitalised word is the noisy
  // category this check exists to stay out of — Landlord, Tenant, Rent,
  // Premises. Two significant words is the floor, before and after.
  return stripped.split(/\s+/).filter(word => word && word !== 'of').length >= 2
    ? stripped
    : null;
}

// Deliberately short. Every entry is a category the filters below cannot see,
// and a long list here would be this check quietly failing in a way nobody
// could audit.
// A term that opens with one of these is a place or a jurisdiction, not
// somebody's forgotten definition. "State of New Jersey" used twice in a
// governing-law clause was reported as an undefined term.
const PLACE_PREFIX =
  /^(?:State|Commonwealth|County|City|Borough|Township|District|Province) of\b/;

const NOT_TERMS = new Set([
  'new york', 'united states', 'north america', 'square feet', 'square foot',
  'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december',
]);

// `Label: value` at the head of a block. This is how the Basic Lease Information
// page defines most of what the body then uses, and it is invisible to the
// quotation-mark forms `definedTermsIn` looks for — the single largest source of
// false "never defined" findings.
const GRID_LABEL = /^\s*([A-Z][^:\n]{1,60}?)\s*:/;

function definedElsewhere(sections) {
  const known = new Set();
  const add = text => {
    const value = String(text || '').trim().toLowerCase();
    if (value) known.add(value);
  };

  sections.forEach(section => {
    // A heading is not a use, and a term that only ever appears as one is a
    // section title rather than a term.
    add(section.title);
    if (section.number) add(section.number);

    blocksOf(section).forEach(block => {
      const grid = GRID_LABEL.exec(block.text);
      if (grid) add(grid[1]);

      // Company names. "Vervalen Realty" is Title Case, multi-word and used on
      // every page, and it is nobody's missing definition.
      const entityRe = new RegExp(ENTITY.source, ENTITY.flags);
      let match;
      while ((match = entityRe.exec(block.text)) !== null) {
        add(nameWithin(match[1].trim().replace(/,+$/, '')));
        add(match[0].trim());
      }
    });
  });

  return known;
}

function undefinedTermFindings(sections, defined) {
  const known = definedElsewhere(sections);
  const seen = new Map();

  sections.forEach((section, order) => {
    (section.paragraphs || []).forEach(paragraph => {
      const text = (paragraph && paragraph.text) || '';
      if (!text.trim()) return;

      const provisional = provisionalRanges(text, paragraph.notes);
      const re = new RegExp(TERM_SHAPED.source, TERM_SHAPED.flags);
      let match;

      while ((match = re.exec(text)) !== null) {
        if (inProvisional(provisional, match.index, match.index + match[0].length))
          continue;

        const term = termWithin(match[0].trim().replace(/\s+/g, ' '));
        if (!term) continue;

        const key = term.toLowerCase();
        if (NOT_TERMS.has(key)) continue;
        if (PLACE_PREFIX.test(term)) continue;
        if (defined.has(key) || known.has(key)) continue;
        // Inside a company name we already recognised, e.g. the "Edens
        // Development" of "Edens Development LLC".
        if ([...known].some(name => name.includes(key))) continue;

        const entry = seen.get(key) || { term, uses: 0, first: null };
        entry.uses += 1;
        if (!entry.first) {
          entry.first = {
            text,
            index: match.index,
            length: match[0].length,
            at: {
              pid: paragraph.pid || section.pid || null,
              number: section.number || null,
              order,
            },
          };
        }
        seen.set(key, entry);
      }
    });
  });

  return [...seen.values()]
    .filter(entry => entry.uses >= USES_BEFORE_REPORTING)
    .map(entry => ({
      id: findingId('integrity_undefined_term', 'document', entry.term),
      family: INTEGRITY_FAMILY,
      check: 'undefined_term',
      severity: 'low',
      summary: `“${entry.term}” is used but never defined`,
      statement:
        `This document uses “${entry.term}” ${entry.uses} times as though it were ` +
        'a defined term, and never introduces it.',
      quote: excerpt(entry.first.text, entry.first.index, entry.first.length),
      at: entry.first.at,
      hits: [{ pid: entry.first.at.pid, text: entry.term }],
      proposal: null,
    }));
}

/**
 * A term this document defines, then writes a different way.
 *
 * Anchored on the DEFINITION rather than left open, which is what makes it
 * safe: we know the canonical spelling because the document declared it, so
 * "base rent" against a defined "Base Rent" is a comparison rather than a guess.
 *
 * Occurrences at the start of a block are skipped. A sentence opens with a
 * capital whatever the term's real casing is, so the first word of a paragraph
 * can never be evidence either way.
 */
function caseDriftFindings(sections, definitions) {
  const canonical = new Map();
  // The blocks that INTRODUCE each term, which are the one place a lowercase
  // use proves nothing. "the premises described above (the “Premises”)" is how
  // a definition is written, not a document contradicting itself — and it was
  // the first thing this check reported before the exclusion existed.
  const definedIn = new Map();

  // Where each term is introduced, so the finding can offer to take somebody
  // there. A drafter reading "the uncapitalised form does not carry the
  // definition" has an obvious next question — what does the definition say —
  // and the answer is a scroll away in a hundred-and-twenty-page document.
  const introducedAt = new Map();

  definitions.forEach(entry => {
    const key = entry.term.toLowerCase();
    canonical.set(key, entry.term);

    const first = entry.where[0];
    if (first && first.at && first.at.pid)
      introducedAt.set(key, {
        pid: first.at.pid,
        number: first.at.number || null,
        // The words that introduce it, so arriving there lights the definition
        // rather than dropping the reader at the top of a clause.
        text: first.hit || null,
      });

    definedIn.set(
      key,
      new Set(entry.where.map(where => where.at && where.at.pid).filter(Boolean)),
    );
  });

  if (!canonical.size) return [];

  const drift = new Map();

  sections.forEach((section, order) => {
    (section.paragraphs || []).forEach(paragraph => {
      const text = (paragraph && paragraph.text) || '';
      if (!text.trim()) return;

      const provisional = provisionalRanges(text, paragraph.notes);

      canonical.forEach((wanted, key) => {
        const where = definedIn.get(key);
        const pid = (paragraph && paragraph.pid) || section.pid || null;
        if (where && where.has(pid)) return;

        const re = new RegExp(`(^|[^A-Za-z])(${escapeForRegex(wanted)})(?![A-Za-z])`, 'gi');
        let match;

        while ((match = re.exec(text)) !== null) {
          const written = match[2];
          if (written === wanted) continue;
          if (inProvisional(provisional, match.index, match.index + match[0].length))
            continue;
          // Sentence-initial, where a capital says nothing about the term.
          const at = match.index + match[1].length;
          if (at === 0) continue;
          if (/[.!?]\s+$/.test(text.slice(0, at))) continue;
          // ALL CAPS is emphasis or a heading fragment, not a different term.
          if (written === written.toUpperCase()) continue;

          const entry = drift.get(key) || { wanted, written, uses: 0, first: null };
          entry.uses += 1;
          if (!entry.first) {
            entry.first = {
              text,
              index: at,
              length: written.length,
              written,
              at: {
                pid: paragraph.pid || section.pid || null,
                number: section.number || null,
                order,
              },
            };
          }
          drift.set(key, entry);
        }
      });
    });
  });

  // A single-word term is also an ordinary noun. "Building" defined, then
  // "any building systems" written once, is drift by the letter of the rule and
  // noise by any other measure — while the case that made this check worth
  // having was "Interest" written lowercase thirty-one times. A multi-word term
  // has no such ambiguity: nobody writes "base rent" by accident.
  const DRIFT_USES_FOR_ONE_WORD = 3;

  return [...drift.values()]
    .filter(
      entry =>
        entry.wanted.split(/\s+/).length > 1 ||
        entry.uses >= DRIFT_USES_FOR_ONE_WORD,
    )
    .map(entry => ({
    id: findingId('integrity_case_drift', 'document', entry.wanted),
    term: entry.wanted,
    definedAt: introducedAt.get(entry.wanted.toLowerCase()) || null,
    family: INTEGRITY_FAMILY,
    check: 'case_drift',
    severity: 'low',
    summary: `“${entry.wanted}” is also written “${entry.first.written}”`,
    statement:
      `This document defines “${entry.wanted}” and then writes it as ` +
      `“${entry.first.written}” ${entry.uses === 1 ? 'once' : `${entry.uses} times`}. ` +
      'The uncapitalised form does not carry the definition.',
    quote: excerpt(entry.first.text, entry.first.index, entry.first.length),
    at: entry.first.at,
    hits: [{ pid: entry.first.at.pid, text: entry.first.written }],
    proposal: null,
  }));
}

function definitionFindings(sections) {
  const definitions = new Map();
  // Every block's text, kept whole, because "never used again" is a claim about
  // the entire document and cannot be answered a section at a time.
  const blocks = [];

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const at = {
        pid: block.pid || section.pid || null,
        number: section.number || null,
        order,
      };
      // Identified by position rather than by pid. A paragraph that carries no
      // pid of its own falls back to its section's, so several blocks can share
      // one — and keyed on pid, a term defined in one paragraph and used in the
      // next would read as never used at all.
      const where = blocks.length;
      blocks.push({ text: block.text, at, where });

      definedTermsIn(block.text, block.notes).forEach(found => {
        const key = found.term.toLowerCase();
        const already = definitions.get(key) || { term: found.term, where: [] };
        // One block introducing the same term twice is one definition written
        // across two clauses of a sentence, not two definitions.
        if (!already.where.some(w => w.where === where))
          already.where.push({ ...found, at, where, blockText: block.text });
        definitions.set(key, already);
      });
    });
  });

  const findings = [];

  definitions.forEach(entry => {
    if (entry.where.length > 1) {
      const [first, second] = entry.where;
      findings.push({
        id: findingId('integrity_defined_twice', 'document', entry.term),
        family: INTEGRITY_FAMILY,
        check: 'defined_twice',
        severity: 'high',
        summary: `“${entry.term}” is defined in ${entry.where.length} places`,
        statement:
          `“${entry.term}” is introduced as a defined term ${entry.where.length} times` +
          `${first.at.number ? `, first in ${first.at.number}` : ''}` +
          `${second.at.number ? ` and again in ${second.at.number}` : ''}. ` +
          'Whichever a reader reaches first is the one that governs.',
        quote: excerpt(second.blockText, second.index, second.hit.length),
        at: second.at,
        // Every place it is introduced, so the drafter compares the definitions
        // rather than taking our word for it that they differ.
        hits: entry.where.map(w => ({ pid: w.at.pid, text: w.hit })),
        proposal: null,
      });
      return;
    }

    const only = entry.where[0];
    if (used(blocks, entry.term, only.where)) return;

    findings.push({
      id: findingId('integrity_defined_unused', 'document', entry.term),
      family: INTEGRITY_FAMILY,
      check: 'defined_unused',
      severity: 'low',
      summary: `“${entry.term}” is defined and never used`,
      statement:
        `“${entry.term}” is introduced as a defined term and does not appear ` +
        'again. Usually what is left when a clause was cut and its definition was not.',
      quote: excerpt(only.blockText, only.index, only.hit.length),
      at: only.at,
      hits: [{ pid: only.at.pid, text: only.hit }],
      proposal: null,
    });
  });

  // The two narrow checks. Both need what this function already collected: the
  // set of terms this document actually introduces. Recomputing it inside each
  // of them would be a second reading of the same document that could disagree
  // with the first.
  const declared = new Set([...definitions.keys()]);

  return findings
    .concat(undefinedTermFindings(sections, declared))
    .concat(caseDriftFindings(sections, [...definitions.values()]));
}

// Anywhere but the block that defines it. Whole-word, because "Rent" is inside
// "Rentable" and a term reported as unused when it is used on the next line is
// the finding that teaches a drafter to stop reading this family.
function used(blocks, term, definedAt) {
  const wanted = escapeForRegex(term);
  const re = new RegExp(`(^|[^A-Za-z])${wanted}([^A-Za-z]|$)`, 'i');

  return blocks.some(block => block.where !== definedAt && re.test(block.text));
}

function escapeForRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ── D. Parties and entities ──────────────────────────────────────────────────

/**
 * lp-016 §3, Family D, the half that can be done without a model.
 *
 * The catalogue's version of this family compares the preamble against the
 * notice block, the signature block and the guaranty. That comparison is a
 * reading, and it is already Family F's job — "notice addresses and the
 * entities they name" is on the contradiction reader's list of facts.
 *
 * What is left is the half that is pure pattern and that a reader would be a
 * waste of money on: the same company named with two different corporate
 * suffixes. "Vervalen Realty LLC" in the preamble and "Vervalen Realty, Inc."
 * on the signature page are two different legal persons, and the one that
 * signed is the one that is bound.
 */
/**
 * Deliberately missing: "limited", "company", "trust" and "association".
 *
 * They are real suffixes — "Acme Limited", "The Vervalen Company" — and they
 * are also the middle of the two phrases every one of these documents contains:
 * "a New York limited partnership" and "a Delaware limited liability company".
 * With them in, a lease whose landlord is a Delaware corporation and whose
 * tenant is a Delaware limited partnership reports that Delaware is two
 * different companies — and "Chase Manhattan Trust" against "Chase Manhattan
 * Corp" is reported HIGH as two legal persons when the first is a department.
 * Missing a rare naming form is the cheaper mistake.
 */
const SUFFIXES = [
  'llc', 'l.l.c.', 'inc', 'inc.', 'incorporated', 'corp', 'corp.', 'corporation',
  'lp', 'l.p.', 'llp', 'l.l.p.', 'ltd', 'ltd.', 'co.',
];

// Case-insensitive on the suffix, case-SENSITIVE on the name. The `i` flag
// would apply to both and turn `[A-Z]` into "any letter", which makes every
// noun before the word "company" into a company. Written out per character
// instead. Longest first, so "Inc." is not matched as "Inc" with the period
// left behind — the hit is quoted back to the drafter and has to read right.
//
// The non-letter branch escapes through `escapeForRegex` rather than handling
// the one metacharacter that happens to appear in `SUFFIXES`. CodeQL flagged
// the narrower version and was right to: `.replace(/\./g, ...)` is an escaper
// that handles a single character and silently passes every other one,
// including a backslash. Nothing reaches this but a hardcoded constant today,
// so there was nothing to exploit — but a partial escaper is a trap for
// whoever first makes suffixes configurable, and the complete one is four
// lines up.
function eitherCase(suffix) {
  return suffix
    .split('')
    .map(character =>
      /[a-z]/.test(character)
        ? `[${character}${character.toUpperCase()}]`
        : escapeForRegex(character),
    )
    .join('');
}

// The comma before the suffix is optional and extremely common —
// "Vervalen Realty, L.L.C." is how half of these are written — and it also does
// the useful work of ending the name: a comma inside the run would let
// "IN WITNESS WHEREOF, Edens Development LLC" become one five-word company, and
// the same company would then not match itself in the preamble.
const ENTITY = new RegExp(
  `((?:[A-Z][A-Za-z&'’.-]*\\s+){0,4}[A-Z][A-Za-z&'’.-]*)\\s*,?\\s+(${SUFFIXES.slice()
    .sort((a, b) => b.length - a.length)
    .map(eitherCase)
    .join('|')})(?![A-Za-z])`,
  'g',
);

/**
 * Boilerplate the run picked up on its way to the name.
 *
 * "IN WITNESS WHEREOF Edens Development LLC" is one all-caps phrase followed by
 * a Title Case company. A name written entirely in capitals is ordinary — half
 * of all preambles do it — so the rule is about MIXING: where a run has both,
 * the leading all-caps words are the sentence and the rest is the company.
 */
function nameWithin(run) {
  const words = run.split(/\s+/).filter(Boolean);
  const shouty = word => word === word.toUpperCase() && /[A-Z]/.test(word);
  if (words.every(shouty) || !words.some(shouty)) return words.join(' ');

  let start = 0;
  while (start < words.length - 1 && shouty(words[start])) start += 1;
  return words.slice(start).join(' ');
}

// Two suffixes are the same suffix when only punctuation and case differ.
// "L.L.C." and "LLC" name one kind of company; "LLC" and "Inc." do not.
function suffixFamily(suffix) {
  const bare = suffix.toLowerCase().replace(/[.\s]/g, '');
  if (bare === 'llc') return 'llc';
  if (bare === 'llp') return 'llp';
  if (bare === 'lp') return 'lp';
  if (['inc', 'incorporated', 'corp', 'corporation'].includes(bare)) return 'corp';
  if (['ltd', 'limited'].includes(bare)) return 'ltd';
  if (['co', 'company'].includes(bare)) return 'company';
  return bare;
}

function entityFindings(sections) {
  const names = new Map();

  sections.forEach((section, order) => {
    blocksOf(section).forEach(block => {
      const re = new RegExp(ENTITY.source, ENTITY.flags);
      let match;

      while ((match = re.exec(block.text)) !== null) {
        const base = nameWithin(match[1].trim().replace(/,+$/, ''));
        // One capitalised word before a suffix is usually a sentence opener
        // that ran into "Company" or "Trust", not a company name.
        if (base.split(' ').length < 2) continue;

        const key = base.toLowerCase().replace(/,/g, '');
        const entry = names.get(key) || { base, forms: new Map() };
        const family = suffixFamily(match[2]);

        if (!entry.forms.has(family)) {
          entry.forms.set(family, {
            hit: match[0].trim(),
            index: match.index,
            blockText: block.text,
            at: {
              pid: block.pid || section.pid || null,
              number: section.number || null,
              order,
            },
          });
        }

        names.set(key, entry);
      }
    });
  });

  const findings = [];

  names.forEach(entry => {
    if (entry.forms.size < 2) return;

    const forms = Array.from(entry.forms.values());
    const first = forms[0];

    findings.push({
      id: findingId('integrity_entity_suffix', 'document', entry.base),
      family: INTEGRITY_FAMILY,
      check: 'entity_suffix',
      severity: 'high',
      summary: `${entry.base} is named as ${forms.length} different entities`,
      statement:
        `${forms.map(f => `“${f.hit}”`).join(' and ')} appear in this document. ` +
        'Those are different legal persons, and the one named in the clause is the one bound by it.',
      quote: excerpt(first.blockText, first.index, first.hit.length),
      at: first.at,
      hits: forms.map(f => ({ pid: f.at.pid, text: f.hit })),
      proposal: null,
    });
  });

  return findings;
}
